import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '@v2/shared/lib/utils';
import SourceLabel from './SourceLabel';
import Stars from './Stars';
import { Review, ReviewSource } from './types';

type Props = {
  review: Review;
  source: ReviewSource;
};

/**
 * One review of a magazine.
 *
 * The product strip across the top is the whole reason this tab was rebuilt: the live
 * widget renders fifty thousand reviews and not one of them says which magazine it is
 * about, so a reader cannot tell whether a one-star complaint is about the title they
 * are considering. The cover, the linked title and that magazine's own average fix that
 * at a glance — and the link hands a reader who is already convinced straight to the
 * page that sells it.
 *
 * The cover carries explicit dimensions so the card does not resize when the image
 * arrives; the reviews below it would otherwise jump on every load.
 */
export default function ProductReviewCard({ review, source }: Props) {
  const product = review.product!;
  const title = product.title ?? 'This magazine';

  return (
    <article className="overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-3.5 border-0 border-b border-solid border-slate-200 bg-slate-50 px-5 py-3">
        {product.image ? (
          <img
            src={product.image}
            alt=""
            width={56}
            height={76}
            loading="lazy"
            className="h-[76px] w-14 shrink-0 rounded object-cover shadow"
          />
        ) : (
          <span
            aria-hidden
            className={cn(
              'flex h-[76px] w-14 shrink-0 items-center justify-center rounded',
              'bg-slate-200 p-1 text-center text-[10px] font-bold uppercase leading-tight text-slate-600'
            )}
          >
            {title}
          </span>
        )}

        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900">
            {product.url ? (
              <a href={product.url} className="text-slate-900 no-underline hover:text-primary hover:underline">
                {title}
              </a>
            ) : (
              title
            )}
          </div>

          {product.reviewCount > 0 && (
            <div className="text-[11px] text-slate-500">
              {product.reviewCount.toLocaleString('en-US')} reviews of this title
            </div>
          )}
        </div>

        {product.averageRating > 0 && (
          <div className="ml-auto whitespace-nowrap text-right text-[11px] text-slate-500">
            <b className="text-sm text-slate-900">{product.averageRating.toFixed(1)}</b>{' '}
            <span className="text-amber-500">★</span>
            <br />
            average
          </div>
        )}
      </div>

      <div className="px-5 pb-4 pt-4">
        <div className="mb-2 flex items-center gap-2.5">
          <span
            aria-hidden
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              'bg-primary-50 text-xs font-bold text-primary'
            )}
          >
            {review.initials}
          </span>

          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{review.author}</div>
            <div className="text-[11px] text-slate-500">
              {review.publishedLabel && (
                <>
                  <time dateTime={review.publishedAt ?? undefined}>{review.publishedLabel}</time>
                  {' · '}
                </>
              )}
              <SourceLabel name={source.name} />
            </div>
          </div>

          {review.verifiedBuyer && (
            <span
              className={cn(
                'ml-auto flex items-center gap-1.5 whitespace-nowrap rounded-full',
                'bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700'
              )}
            >
              <FontAwesomeIcon icon={faCheck} aria-hidden /> Verified subscriber
            </span>
          )}
        </div>

        <Stars rating={review.rating} className="mb-1.5 text-sm" />

        {review.title && <h3 className="mb-1 text-sm font-bold text-slate-900">{review.title}</h3>}

        {review.body && <p className="m-0 text-sm text-slate-600">{review.body}</p>}

        {review.helpfulVotes > 0 && (
          <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-slate-500">
            <FontAwesomeIcon icon={faThumbsUp} aria-hidden />
            {review.helpfulVotes.toLocaleString('en-US')} found this helpful
          </div>
        )}
      </div>
    </article>
  );
}
